import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  WEBSITE_PRICING_COMPARISON,
  WEBSITE_TESTIMONIALS,
} from './websiteData';

const TIERS = [
  {
    id: 'community', name: 'Community',
    persona: '👤 Individual / solo',
    monthlyPrice: 0, annualMonthly: 0,
    priceLabel: '€0', priceSub: 'Free forever',
    note: 'Evaluate DepGuard with your own SBOMs. No credit card, no time limit.',
    cta: 'Start free', ctaTo: '/login', ctaStyle: 'soft',
    features: ['1 repository','1 SBOM upload per day','OSV.dev vulnerability scanning','Basic license detection','Community support via docs'],
    notIncluded: ['EU CRA reports','CI/CD integration','Policy engine'],
  },
  {
    id: 'starter', name: 'Starter',
    persona: '👥 Small engineering team',
    monthlyPrice: 29, annualMonthly: 24,
    priceLabel: '€29', annualLabel: '€24', priceSub: '/ month',
    note: 'Full scanning, CI/CD gates, and remediation for teams of 2–10 engineers.',
    cta: 'Start 14-day trial', ctaTo: '/trial?plan=starter', ctaStyle: 'secondary',
    features: ['10 repositories','Unlimited SBOM uploads','License risk + package health (OpenSSF)','SBOM quality scoring A–F','SBOM diff (added / removed)','VEX statements (CycloneDX)','PDF report export','Policy engine (preset rules)','CI/CD gate — 6 ecosystems','Remediation tracking','Email support'],
    notIncluded: ['EU CRA audit reports','Custom policies','AI assistant'],
  },
  {
    id: 'professional', name: 'Professional',
    persona: '🏢 Security / compliance team',
    monthlyPrice: 99, annualMonthly: 82,
    priceLabel: '€99', annualLabel: '€82', priceSub: '/ month',
    popular: true,
    note: 'EU CRA compliance, AI triage, and full policy control for teams with regulatory obligations.',
    cta: 'Start 14-day trial', ctaTo: '/trial?plan=professional', ctaStyle: 'primary',
    features: ['100 repositories','Everything in Starter','EU CRA Article 20 audit reports','NIST SP 800-53 + ISO 27001 mapping','SLSA provenance verification','Malware & typosquatting detection','Reachability analysis','Custom policies','Webhook alerts (Slack, Teams, HMAC)','AI assistant — multi-model','Cross-project package impact graph','Priority support'],
    notIncluded: ['SSO / SAML','On-premises','Dedicated CSM'],
  },
  {
    id: 'enterprise', name: 'Enterprise',
    persona: '🏦 Large org / regulated industry',
    monthlyPrice: null, annualMonthly: null,
    priceLabel: 'Custom', priceSub: 'per agreement',
    note: 'Unlimited scale, SSO, on-premises deployment, and a dedicated success team.',
    cta: 'Talk to sales', ctaTo: '/website/contact', ctaStyle: 'dark',
    features: ['Unlimited repositories','Everything in Professional','SSO / SAML / Azure AD','On-premises deployment','Custom SLA','Dedicated Customer Success Manager','Volume licensing','Custom policy packs','Priority onboarding','Executive briefings'],
    notIncluded: [],
  },
];

const PRICING_FAQ = [
  { q: 'Is there a free trial?', a: 'Yes — the Community tier is free forever. Professional and Starter both include a 14-day free trial with full feature access. No credit card required.' },
  { q: 'How does annual billing work?', a: 'Annual billing gives you 20% off — effectively two months free. You pay once per year; the subscription auto-renews. Cancel before renewal for a prorated refund.' },
  { q: 'What is included in the EU CRA audit report?', a: 'The report validates all mandatory Article 20 fields per SBOM — PURL, supplier, hash, dependency relationships, and timestamp. It produces a downloadable PDF with compliance percentage, gap list, and remediation guidance. Available in Professional and Enterprise.' },
  { q: 'Can I upgrade or downgrade at any time?', a: 'Yes. Upgrades are effective immediately and prorated. Downgrades take effect at the next billing cycle. Your data is always preserved.' },
  { q: 'Is on-premises deployment available?', a: 'Yes, on-premises is available in Enterprise. DepGuard ships as a single Go binary with zero external runtime dependencies — no JVM, no Python, no Docker required.' },
  { q: 'What payment methods do you accept?', a: 'All major credit / debit cards, SEPA Direct Debit for EU customers, and bank transfer for annual Enterprise contracts. Payments via Stripe.' },
  { q: 'Do you offer discounts for non-profits or startups?', a: 'Yes — 50% off for verified non-profits and startups under €2M ARR. Contact sales@lexint.io.' },
];

const ENTERPRISE_CAPABILITIES = [
  { icon: '🇪🇺', title: 'EU CRA audit report — one click', desc: 'Validates all Article 20 fields. Downloadable evidence pack. 3 weeks of manual work → 60 seconds.' },
  { icon: '🕸️', title: 'Cross-project impact graph', desc: 'Every repository affected by one CVE. Portfolio-wide blast-radius for risk decisions.' },
  { icon: '🔐', title: 'CI/CD policy gates', desc: 'Block non-compliant builds in GitHub Actions, GitLab CI, Azure DevOps. 6 ecosystems.' },
  { icon: '🤖', title: 'AI-powered triage', desc: 'Multi-model AI (Copilot SDK, OpenAI, Anthropic, Ollama) cuts CVE triage time by 80%.' },
  { icon: '🏥', title: 'Webhook alert engine', desc: 'Real-time Slack, Teams, and custom endpoints with HMAC signing and delivery logs.' },
  { icon: '📋', title: 'Full audit trail', desc: 'Every vulnerability decision logged with actor, timestamp, and rationale.' },
  { icon: '🧭', title: 'Reachability analysis', desc: 'Are vulnerable packages actually called in your code? Cuts false-positive noise by 80%.' },
  { icon: '🔍', title: 'SLSA provenance verification', desc: 'Accept in-toto attestations. Display SLSA level 1–3 per artifact. Required by EU CRA.' },
];

const ROI_METRICS = [
  { value: '80%', label: 'Reduction in CVE triage time', desc: 'AI triage and reachability analysis direct attention to vulnerabilities that actually matter.' },
  { value: '60s', label: 'To generate an EU CRA audit report', desc: 'What used to require 3 weeks of manual field validation now runs in one click per SBOM.' },
  { value: '€15M', label: 'Maximum EU CRA penalty avoided', desc: 'Article 20 non-compliance carries penalties up to €15M or 2.5% of global annual turnover.' },
];

function renderCompareCellValue(value) {
  if (value === true)  return <span style={{color:'#10b981', fontWeight:700, fontSize:16}}>✓</span>;
  if (value === false) return <span style={{color:'#334155', fontSize:18}}>–</span>;
  return <span style={{fontSize:13, fontWeight:600, color:'#e2e8f0'}}>{value}</span>;
}

function FAQ({ items }) {
  const [open, setOpen] = useState(null);
  return (
    <div style={{display:'flex', flexDirection:'column', gap:2}}>
      {items.map((item, i) => (
        <div key={i} style={{borderBottom:'1px solid rgba(255,255,255,.07)'}}>
          <button onClick={() => setOpen(open === i ? null : i)}
            style={{width:'100%', textAlign:'left', background:'transparent', border:'none', padding:'20px 0', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', gap:16}}>
            <span style={{fontSize:16, fontWeight:600, color:'#e2e8f0', lineHeight:1.4}}>{item.q}</span>
            <span style={{fontSize:22, color:'#6366f1', lineHeight:1, flexShrink:0, display:'inline-block', transform: open === i ? 'rotate(45deg)' : 'none', transition:'transform .2s ease'}}>+</span>
          </button>
          {open === i && (
            <p style={{fontSize:15, color:'#94a3b8', lineHeight:1.7, marginBottom:20, marginTop:0}}>{item.a}</p>
          )}
        </div>
      ))}
    </div>
  );
}

export default function WebsitePricingPage() {
  const [annual, setAnnual] = useState(false);

  return (
    <div style={{background:'#0d1117', color:'#e2e8f0', minHeight:'100vh'}}>

      {/* Hero */}
      <section style={{padding:'clamp(60px,10vw,120px) clamp(20px,5vw,60px) 100px', textAlign:'center', background:'linear-gradient(180deg, #0d1117 0%, #0f172a 100%)', borderBottom:'1px solid rgba(255,255,255,.06)'}}>
        <span style={{display:'inline-block', background:'rgba(99,102,241,.15)', border:'1px solid rgba(99,102,241,.3)', borderRadius:100, padding:'5px 16px', fontSize:12, fontWeight:700, color:'#818cf8', letterSpacing:'.05em', textTransform:'uppercase', marginBottom:24}}>Pricing</span>
        <h1 style={{fontSize:'clamp(36px,5vw,64px)', fontWeight:900, color:'#f1f5f9', lineHeight:1.1, letterSpacing:'-.03em', maxWidth:700, margin:'0 auto 20px'}}>
          Simple pricing.<br/>Serious security.
        </h1>
        <p style={{fontSize:18, color:'#94a3b8', lineHeight:1.7, maxWidth:520, margin:'0 auto 40px'}}>
          Start free. Upgrade when your team needs EU CRA compliance, AI triage, or enterprise scale. Cancel anytime.
        </p>
        <div style={{display:'flex', gap:24, justifyContent:'center', flexWrap:'wrap', marginBottom:48}}>
          {['No credit card required','14-day free trial','Cancel anytime','EU data residency'].map(b => (
            <span key={b} style={{fontSize:14, color:'#64748b', fontWeight:600}}>✓ {b}</span>
          ))}
        </div>
        <div style={{display:'inline-flex', background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', borderRadius:100, padding:4, gap:4, alignItems:'center'}}>
          <button onClick={() => setAnnual(false)} style={{padding:'8px 24px', borderRadius:100, border:'none', background:!annual ? '#6366f1' : 'transparent', color:!annual ? '#fff' : '#64748b', fontWeight:700, fontSize:14, cursor:'pointer', fontFamily:'inherit'}}>Monthly</button>
          <button onClick={() => setAnnual(true)} style={{padding:'8px 24px', borderRadius:100, border:'none', background:annual ? '#6366f1' : 'transparent', color:annual ? '#fff' : '#64748b', fontWeight:700, fontSize:14, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:8}}>
            Annual
            <span style={{background:'#10b981', color:'#fff', borderRadius:100, padding:'2px 8px', fontSize:11, fontWeight:800}}>SAVE 20%</span>
          </button>
        </div>
      </section>

      {/* Tier cards */}
      <section style={{padding:'0 clamp(20px,5vw,60px)', maxWidth:1360, margin:'0 auto', transform:'translateY(-48px)'}}>
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(260px, 1fr))', gap:16}}>
          {TIERS.map((tier) => {
            const price = annual && tier.annualMonthly !== null
              ? (tier.annualMonthly === 0 ? '€0' : ('€' + tier.annualMonthly))
              : tier.priceLabel;
            const isPopular = tier.popular;
            return (
              <div key={tier.id} style={{background: isPopular ? 'linear-gradient(145deg, #1e1b4b, #1a1f35)' : 'rgba(255,255,255,.04)', border: isPopular ? '2px solid #6366f1' : '1px solid rgba(255,255,255,.08)', borderRadius:20, padding:'32px 28px', display:'flex', flexDirection:'column', position:'relative', boxShadow: isPopular ? '0 0 60px rgba(99,102,241,.2)' : 'none'}}>
                {isPopular && (
                  <div style={{position:'absolute', top:-14, left:'50%', transform:'translateX(-50%)', background:'linear-gradient(90deg, #6366f1, #8b5cf6)', color:'#fff', borderRadius:100, padding:'4px 18px', fontSize:12, fontWeight:800, letterSpacing:'.06em', textTransform:'uppercase', whiteSpace:'nowrap'}}>⭐ Most popular</div>
                )}
                <span style={{fontSize:12, color:'#64748b', fontWeight:600, marginBottom:8}}>{tier.persona}</span>
                <h2 style={{fontSize:22, fontWeight:800, color:'#f1f5f9', marginBottom:12}}>{tier.name}</h2>
                <div style={{marginBottom:6, display:'flex', alignItems:'baseline', gap:4}}>
                  <span style={{fontSize:48, fontWeight:900, color:'#f1f5f9', lineHeight:1, letterSpacing:'-.03em'}}>{price}</span>
                  <span style={{fontSize:14, color:'#64748b'}}>{tier.monthlyPrice !== null ? '/ month' : tier.priceSub}</span>
                </div>
                {annual && tier.monthlyPrice > 0 && tier.annualMonthly !== null && (
                  <p style={{fontSize:12, color:'#10b981', fontWeight:600, marginBottom:6}}>
                    {'Billed €' + (tier.annualMonthly * 12) + '/yr — save €' + ((tier.monthlyPrice - tier.annualMonthly) * 12) + '/yr'}
                  </p>
                )}
                <p style={{fontSize:13, color:'#94a3b8', lineHeight:1.6, marginBottom:24}}>{tier.note}</p>
                <Link to={tier.ctaTo} style={{display:'block', textAlign:'center', padding:'12px 24px', borderRadius:10, fontWeight:700, fontSize:14, textDecoration:'none', marginBottom:28,
                  ...(tier.ctaStyle === 'primary' ? {background:'linear-gradient(135deg, #6366f1, #8b5cf6)', color:'#fff'}
                  : tier.ctaStyle === 'secondary' ? {background:'rgba(99,102,241,.15)', color:'#818cf8', border:'1px solid rgba(99,102,241,.3)'}
                  : tier.ctaStyle === 'dark' ? {background:'#0f172a', color:'#e2e8f0', border:'1px solid rgba(255,255,255,.12)'}
                  : {background:'rgba(255,255,255,.06)', color:'#94a3b8', border:'1px solid rgba(255,255,255,.1)'}),
                }}>{tier.cta} →</Link>
                <div style={{borderTop:'1px solid rgba(255,255,255,.07)', marginBottom:20}} />
                <ul style={{listStyle:'none', padding:0, margin:0, display:'flex', flexDirection:'column', gap:10}}>
                  {tier.features.map(f => (
                    <li key={f} style={{display:'flex', gap:10, alignItems:'flex-start'}}>
                      <span style={{color:'#6366f1', fontWeight:800, flexShrink:0, lineHeight:1.5}}>✓</span>
                      <span style={{fontSize:13, color:'#cbd5e1', lineHeight:1.5}}>{f}</span>
                    </li>
                  ))}
                  {tier.notIncluded.map(f => (
                    <li key={f} style={{display:'flex', gap:10, alignItems:'flex-start', opacity:.35}}>
                      <span style={{color:'#475569', fontWeight:800, flexShrink:0, lineHeight:1.5}}>✗</span>
                      <span style={{fontSize:13, color:'#64748b', lineHeight:1.5}}>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* EU CRA urgency */}
      <section style={{maxWidth:1360, margin:'0 auto', padding:'0 clamp(20px,5vw,60px) 72px'}}>
        <div style={{background:'linear-gradient(135deg, rgba(99,102,241,.12), rgba(6,182,212,.08))', border:'1px solid rgba(99,102,241,.3)', borderRadius:16, padding:'28px 36px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:24, flexWrap:'wrap'}}>
          <div style={{display:'flex', alignItems:'center', gap:16}}>
            <span style={{fontSize:32}}>🇪🇺</span>
            <div>
              <div style={{fontWeight:800, fontSize:16, color:'#e2e8f0', marginBottom:4}}>EU CRA compliance deadline: late 2027</div>
              <div style={{fontSize:14, color:'#94a3b8'}}>Article 20 penalties reach €15M. Professional tier includes one-click audit reports.</div>
            </div>
          </div>
          <Link to='/website/eu-cra' style={{background:'linear-gradient(135deg, #6366f1, #8b5cf6)', color:'#fff', borderRadius:10, padding:'12px 24px', fontWeight:700, fontSize:14, textDecoration:'none', whiteSpace:'nowrap', flexShrink:0}}>Read the EU CRA guide →</Link>
        </div>
      </section>

      {/* ROI */}
      <section style={{padding:'80px clamp(20px,5vw,60px)', borderTop:'1px solid rgba(255,255,255,.05)'}}>
        <div style={{maxWidth:1360, margin:'0 auto'}}>
          <div style={{textAlign:'center', marginBottom:52}}>
            <span style={{fontSize:12, fontWeight:700, color:'#6366f1', textTransform:'uppercase', letterSpacing:'.1em'}}>Return on investment</span>
            <h2 style={{fontSize:36, fontWeight:800, color:'#f1f5f9', marginTop:12}}>What you get back</h2>
          </div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:24}}>
            {ROI_METRICS.map(m => (
              <div key={m.value} style={{background:'rgba(255,255,255,.03)', border:'1px solid rgba(255,255,255,.08)', borderRadius:16, padding:'36px 32px', textAlign:'center'}}>
                <div style={{fontSize:56, fontWeight:900, color:'#6366f1', lineHeight:1, letterSpacing:'-.03em', marginBottom:8}}>{m.value}</div>
                <div style={{fontSize:15, fontWeight:700, color:'#e2e8f0', marginBottom:12}}>{m.label}</div>
                <p style={{fontSize:14, color:'#64748b', lineHeight:1.6, margin:0}}>{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature comparison */}
      <section style={{padding:'80px clamp(20px,5vw,60px)', borderTop:'1px solid rgba(255,255,255,.05)'}}>
        <div style={{maxWidth:1360, margin:'0 auto'}}>
          <div style={{textAlign:'center', marginBottom:48}}>
            <span style={{fontSize:12, fontWeight:700, color:'#6366f1', textTransform:'uppercase', letterSpacing:'.1em'}}>Full comparison</span>
            <h2 style={{fontSize:36, fontWeight:800, color:'#f1f5f9', marginTop:12}}>What's included in each plan</h2>
            <p style={{fontSize:16, color:'#64748b', marginTop:12}}>Every feature, across every tier. No surprises.</p>
          </div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%', borderCollapse:'collapse', fontSize:14}}>
              <thead>
                <tr>
                  <th style={{textAlign:'left', padding:'16px 20px', color:'#64748b', fontWeight:600, fontSize:13, borderBottom:'1px solid rgba(255,255,255,.08)', minWidth:200}}>Capability</th>
                  {['Community','Starter','Professional','Enterprise'].map((h, i) => (
                    <th key={h} style={{textAlign:'center', padding:'16px 12px', borderBottom:'1px solid rgba(255,255,255,.08)', color: i === 2 ? '#818cf8' : '#94a3b8', fontWeight: i === 2 ? 800 : 600, fontSize:13, minWidth:110}}>
                      {h}
                      {i === 2 && <div style={{fontSize:10, color:'#6366f1', fontWeight:700, textTransform:'uppercase', marginTop:2}}>Most popular</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {WEBSITE_PRICING_COMPARISON.flatMap((group) => [
                  <tr key={'s-' + group.section}>
                    <td colSpan={5} style={{padding:'20px 20px 8px', fontSize:11, fontWeight:800, color:'#6366f1', textTransform:'uppercase', letterSpacing:'.1em', borderTop:'1px solid rgba(255,255,255,.05)'}}>{group.section}</td>
                  </tr>,
                  ...group.rows.map((row) => (
                    <tr key={group.section + '-' + row.label} style={{borderBottom:'1px solid rgba(255,255,255,.04)'}}>
                      <td style={{padding:'14px 20px', color:'#cbd5e1', fontSize:14}}>{row.label}</td>
                      {[row.community, row.starter, row.professional, row.enterprise].map((val, ci) => (
                        <td key={ci} style={{textAlign:'center', padding:'14px 12px', background: ci === 2 ? 'rgba(99,102,241,.04)' : 'transparent'}}>{renderCompareCellValue(val)}</td>
                      ))}
                    </tr>
                  )),
                ])}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Who is it for */}
      <section style={{padding:'80px clamp(20px,5vw,60px)', background:'rgba(255,255,255,.02)', borderTop:'1px solid rgba(255,255,255,.05)'}}>
        <div style={{maxWidth:1360, margin:'0 auto'}}>
          <div style={{textAlign:'center', marginBottom:48}}>
            <span style={{fontSize:12, fontWeight:700, color:'#6366f1', textTransform:'uppercase', letterSpacing:'.1em'}}>Buyer fit</span>
            <h2 style={{fontSize:36, fontWeight:800, color:'#f1f5f9', marginTop:12}}>Which plan is right for you?</h2>
          </div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', gap:20}}>
            {[
              {icon:'👤', tier:'Community', colour:'#94a3b8', forLabel:'Security engineer evaluating DepGuard solo', when:'You need to scan a handful of SBOMs and see if DepGuard fits your workflow.', upgrade:'Move to Starter when your team joins or you need CI/CD gates.'},
              {icon:'👥', tier:'Starter', colour:'#34d399', forLabel:'Small DevSecOps team (2–10 engineers)', when:'You need full scanning, license compliance, and automated CI/CD gates across 10 repos.', upgrade:'Move to Professional when EU CRA reports or custom policies become a requirement.'},
              {icon:'🏢', tier:'Professional', colour:'#818cf8', forLabel:'Security or compliance team at a regulated company', when:'You have EU CRA obligations, need AI-powered triage, and manage 10–100 repositories.', upgrade:'Move to Enterprise when you need SSO, on-premises, or unlimited repos.'},
              {icon:'🏦', tier:'Enterprise', colour:'#06b6d4', forLabel:'Large org, regulated industry, or multi-team rollout', when:'You need SSO, on-premises deployment, dedicated support, and volume licensing.', upgrade:''},
            ].map(p => (
              <div key={p.tier} style={{background:'rgba(255,255,255,.03)', border:'1px solid rgba(255,255,255,.08)', borderTop:'3px solid ' + p.colour, borderRadius:16, padding:'28px 24px'}}>
                <div style={{fontSize:28, marginBottom:12}}>{p.icon}</div>
                <div style={{fontSize:12, fontWeight:700, color:p.colour, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8}}>{p.tier}</div>
                <div style={{fontSize:15, fontWeight:700, color:'#e2e8f0', marginBottom:12, lineHeight:1.4}}>{p.forLabel}</div>
                <p style={{fontSize:13, color:'#64748b', lineHeight:1.6, margin:'0 0 12px'}}>{p.when}</p>
                {p.upgrade ? <p style={{fontSize:12, color:'#475569', lineHeight:1.5, margin:0, fontStyle:'italic'}}>💡 {p.upgrade}</p> : null}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise */}
      <section style={{padding:'80px clamp(20px,5vw,60px)', borderTop:'1px solid rgba(255,255,255,.05)'}}>
        <div style={{maxWidth:1360, margin:'0 auto'}}>
          <div className="dgw-2col" style={{gap:64, alignItems:'start'}}>
            <div>
              <span style={{fontSize:12, fontWeight:700, color:'#06b6d4', textTransform:'uppercase', letterSpacing:'.1em'}}>Enterprise</span>
              <h2 style={{fontSize:40, fontWeight:900, color:'#f1f5f9', lineHeight:1.1, marginTop:12, marginBottom:20}}>Built for teams with<br/>governance obligations.</h2>
              <p style={{fontSize:16, color:'#94a3b8', lineHeight:1.7, marginBottom:32}}>DepGuard Enterprise gives your security and compliance teams the evidence, controls, and integrations that regulated industries and EU CRA auditors actually require.</p>
              <div style={{display:'flex', gap:12, flexWrap:'wrap'}}>
                <Link to='/website/contact' style={{background:'linear-gradient(135deg, #6366f1, #8b5cf6)', color:'#fff', borderRadius:10, padding:'14px 28px', fontWeight:700, fontSize:15, textDecoration:'none'}}>Talk to sales →</Link>
                <Link to='/website/eu-cra' style={{background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.12)', color:'#94a3b8', borderRadius:10, padding:'14px 28px', fontWeight:600, fontSize:15, textDecoration:'none'}}>EU CRA guide</Link>
              </div>
            </div>
            <div className="dgw-2col" style={{gap:16, alignItems:'start'}}>
              {ENTERPRISE_CAPABILITIES.map(cap => (
                <div key={cap.title} style={{background:'rgba(255,255,255,.03)', border:'1px solid rgba(255,255,255,.07)', borderRadius:12, padding:'20px 18px'}}>
                  <div style={{fontSize:24, marginBottom:8}}>{cap.icon}</div>
                  <div style={{fontSize:13, fontWeight:700, color:'#e2e8f0', marginBottom:6, lineHeight:1.3}}>{cap.title}</div>
                  <p style={{fontSize:12, color:'#64748b', lineHeight:1.5, margin:0}}>{cap.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section style={{padding:'80px clamp(20px,5vw,60px)', background:'rgba(255,255,255,.02)', borderTop:'1px solid rgba(255,255,255,.05)'}}>
        <div style={{maxWidth:1360, margin:'0 auto'}}>
          <div style={{textAlign:'center', marginBottom:48}}>
            <span style={{fontSize:12, fontWeight:700, color:'#6366f1', textTransform:'uppercase', letterSpacing:'.1em'}}>Social proof</span>
            <h2 style={{fontSize:36, fontWeight:800, color:'#f1f5f9', marginTop:12}}>What teams say</h2>
          </div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:24}}>
            {WEBSITE_TESTIMONIALS.map((t) => (
              <div key={t.name} style={{background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', borderRadius:16, padding:'32px 28px'}}>
                <p style={{fontSize:48, color:'#6366f1', lineHeight:1, fontFamily:'Georgia,serif', margin:'0 0 12px'}}>&quot;</p>
                <p style={{fontSize:15, color:'#cbd5e1', lineHeight:1.7, marginBottom:24}}>{t.quote}</p>
                <div style={{borderTop:'1px solid rgba(255,255,255,.07)', paddingTop:16}}>
                  <div style={{fontWeight:700, color:'#e2e8f0', fontSize:14}}>{t.name}</div>
                  <div style={{fontSize:13, color:'#64748b'}}>{t.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{padding:'80px clamp(20px,5vw,60px)', borderTop:'1px solid rgba(255,255,255,.05)'}}>
        <div style={{maxWidth:760, margin:'0 auto'}}>
          <div style={{textAlign:'center', marginBottom:48}}>
            <span style={{fontSize:12, fontWeight:700, color:'#6366f1', textTransform:'uppercase', letterSpacing:'.1em'}}>FAQ</span>
            <h2 style={{fontSize:36, fontWeight:800, color:'#f1f5f9', marginTop:12}}>Pricing questions</h2>
          </div>
          <FAQ items={PRICING_FAQ} />
          <div style={{textAlign:'center', marginTop:40}}>
            <p style={{color:'#64748b', fontSize:14, marginBottom:16}}>Still have questions? We respond within one business day.</p>
            <Link to='/website/contact' style={{color:'#818cf8', fontWeight:600, fontSize:14, textDecoration:'none', borderBottom:'1px solid rgba(129,140,248,.3)'}}>Contact us →</Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{padding:'80px clamp(20px,5vw,60px)', background:'linear-gradient(135deg, #1e1b4b, #0f172a)', borderTop:'1px solid rgba(99,102,241,.2)', textAlign:'center'}}>
        <div style={{maxWidth:640, margin:'0 auto'}}>
          <h2 style={{fontSize:40, fontWeight:900, color:'#f1f5f9', lineHeight:1.1, marginBottom:16}}>Start securing your supply chain today.</h2>
          <p style={{fontSize:17, color:'#94a3b8', lineHeight:1.7, marginBottom:40}}>
            Community is free forever. The Professional 14-day trial gives you access to every feature. No credit card, no sales call.
          </p>
          <div style={{display:'flex', gap:16, justifyContent:'center', flexWrap:'wrap', marginBottom:32}}>
            <Link to='/trial?plan=professional' style={{background:'linear-gradient(135deg, #6366f1, #8b5cf6)', color:'#fff', borderRadius:12, padding:'16px 36px', fontWeight:800, fontSize:16, textDecoration:'none'}}>Start 14-day free trial →</Link>
            <Link to='/website/contact' style={{background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.12)', color:'#e2e8f0', borderRadius:12, padding:'16px 36px', fontWeight:700, fontSize:16, textDecoration:'none'}}>Talk to sales</Link>
          </div>
          <p style={{fontSize:13, color:'#475569'}}>✓ 14 days free · ✓ No credit card · ✓ Cancel anytime · ✓ EU data residency</p>
        </div>
      </section>

    </div>
  );
}
